import { apiClient } from '@shared/api';
import type { ApiResponse } from '@shared/types';
import type {
  DeadLetterJob,
  DocScanHealth,
  HttpTrends,
  NetSuiteTrends,
  OutboxLiveStats,
  OutboxTrends,
  TimeRange,
} from '../types/systemHealth.types';

/** Range → `days` param for the outbox/NetSuite trend endpoints. */
function rangeToDays(range: TimeRange): number {
  switch (range) {
    case '24h':
      return 1;
    case '7d':
      return 7;
    case '30d':
      return 30;
    case '90d':
      return 90;
  }
}

/** Range → `hours` param for the HTTP trend endpoint. Capped at 720 (30d) — the request-stats retention
 * window; the API-traffic section notes it only covers the last 30 days at the 90d range. */
function rangeToHours(range: TimeRange): number {
  switch (range) {
    case '24h':
      return 24;
    case '7d':
      return 168;
    case '30d':
    case '90d':
      return 720;
  }
}

/**
 * System Health dashboard API (86d3peur0 Phase C) — read-only aggregate reads over the async work outbox,
 * NetSuite-call telemetry, and inbound HTTP stats. All gated server-side by `system.view_health`. The live
 * concurrency-gate view is a separate SSE stream (see `useGateStream`).
 */
class SystemHealthService {
  async getOutboxLive(): Promise<OutboxLiveStats> {
    const { data } = await apiClient.get<ApiResponse<OutboxLiveStats>>('/admin/health/outbox/live');
    if (data.success && data.data) return data.data;
    throw new Error(data.message || 'Failed to load outbox live stats');
  }

  async getOutboxTrends(range: TimeRange): Promise<OutboxTrends> {
    const { data } = await apiClient.get<ApiResponse<OutboxTrends>>('/admin/health/outbox/trends', {
      params: { days: rangeToDays(range) },
    });
    if (data.success && data.data) return data.data;
    throw new Error(data.message || 'Failed to load outbox trends');
  }

  async getDeadLetters(): Promise<DeadLetterJob[]> {
    const { data } = await apiClient.get<ApiResponse<DeadLetterJob[]>>('/admin/health/outbox/dead-letters');
    if (data.success && data.data) return data.data;
    throw new Error(data.message || 'Failed to load dead-letter jobs');
  }

  async getNetSuiteTrends(range: TimeRange): Promise<NetSuiteTrends> {
    const { data } = await apiClient.get<ApiResponse<NetSuiteTrends>>('/admin/health/netsuite/trends', {
      params: { days: rangeToDays(range) },
    });
    if (data.success && data.data) return data.data;
    throw new Error(data.message || 'Failed to load NetSuite trends');
  }

  async getHttpTrends(range: TimeRange): Promise<HttpTrends> {
    const { data } = await apiClient.get<ApiResponse<HttpTrends>>('/admin/health/http/trends', {
      params: { hours: rangeToHours(range) },
    });
    if (data.success && data.data) return data.data;
    throw new Error(data.message || 'Failed to load HTTP trends');
  }

  /** Document Capture health (86d3rrfee) — one aggregate: lane posture, review backlog, stations, sweep. */
  async getDocumentCaptureHealth(): Promise<DocScanHealth> {
    const { data } = await apiClient.get<ApiResponse<DocScanHealth>>('/admin/health/document-capture');
    if (data.success && data.data) return data.data;
    throw new Error(data.message || 'Failed to load Document Capture health');
  }
}

export const systemHealthService = new SystemHealthService();
